import React from "react";
import { Co2Equivalences } from "@signolteam/shared/co2-equivalences";
import { ImageWrapper } from "@src/components/atoms";
import { formatNumber } from "@signolteam/shared/units-of-measure";
import { useDCContext } from "@src/context";
import CardContainer from "../CardContainer";
import { Icon, Tooltip, CarouselSignol } from "@src/components/atoms";
import { ManagersBehaviourChangeCard } from "@src/components/cards/manager";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";

const BasicCardContent = (props) => {
  const { dataJSON, industry, customerCode } = props;
  const dc = useDCContext();
  const equivalence = dataJSON.equivalency
    ? new Co2Equivalences().computeOne(
        industry,
        dataJSON.co2Saved,
        "global",
        customerCode,
      )
    : null;

  // Use statisticallySignificantValue if available, otherwise fall back to totalValue for backwards compatibility
  if (isNaN(dataJSON.statisticallySignificantValue)) {
    dataJSON.statisticallySignificantValue = Number(dataJSON.totalValue);
  }
  return (
    <div className="h-full px-6 py-3 flex flex-row justify-between items-center">
      {dataJSON.currency && (
        <>
          <div>
            <div>
              <span className="pr-1 text-4xl">{`${formatNumber(dataJSON.statisticallySignificantValue, { precision: 2 })}`}</span>
              <span>{dataJSON.currency}</span>
            </div>
            <div className="flex items-center">
              {!dataJSON.subtext.includes("donation") &&
                dataJSON.globallySignificant && (
                  <Icon
                    name="triangle"
                    size="sm"
                    backgroundColor="transparent"
                    textColor={
                      dataJSON.statisticallySignificantValue > 0
                        ? "primary"
                        : "red"
                    }
                    padding={false}
                    centered={false}
                    className={
                      dataJSON.statisticallySignificantValue > 0
                        ? "pr-1"
                        : "rotate-180 pl-1"
                    }
                  />
                )}
              <span>
                {dc.getDynamicReplacement(
                  dataJSON.subtext.replace("{{", "").replace("}}", ""),
                )}
              </span>
            </div>
          </div>
          {dataJSON.tooltip && (
            <div className="w-6 flex justify-center items-center">
              <Tooltip
                content={[dc.getDynamicReplacement(dataJSON.tooltip)]}
                direction="up"
              />
            </div>
          )}
        </>
      )}
      {dataJSON.roi_percentage > 0 && (
        <div>
          <div className="text-4xl">{`${formatNumber(dataJSON.roi_percentage)}%`}</div>
          <div className="flex items-center">
            {dataJSON.globallySignificant && (
              <Icon
                name="triangle"
                size="sm"
                backgroundColor="transparent"
                textColor={dataJSON.roi_percentage > 0 ? "primary" : "red"}
                padding={false}
                centered={false}
                className={
                  dataJSON.roi_percentage > 0 ? "pr-1" : "rotate-180 pl-1"
                }
              />
            )}
            <span>
              {dc.getDynamicReplacement(
                dataJSON.subtext.replace("{{", "").replace("}}", ""),
              )}
            </span>
          </div>
        </div>
      )}
      {!isNaN(dataJSON.statisticallySignificantValue) &&
        !dataJSON.subtext.includes("fuel_cost_savings_card") &&
        !dataJSON.subtext.includes("donations_card") && (
          <div>
            <div className="text-4xl">
              <span
                className={
                  dataJSON.subtext.includes("fuel_savings_card")
                    ? `${dataJSON.trendIncrease ? "text-primary-500" : "text-red-500"}`
                    : ""
                }
              >{`${formatNumber(dataJSON.statisticallySignificantValue)} `}</span>
              <span>{`${dataJSON.unitOfMeasure}`}</span>
            </div>
            <div className="flex items-center">
              {dataJSON.globallySignificant && (
                <Icon
                  name="triangle"
                  size="sm"
                  backgroundColor="transparent"
                  textColor={dataJSON.trendIncrease ? "primary" : "red"}
                  padding={false}
                  centered={false}
                  className={
                    dataJSON.trendIncrease ? "pr-1" : "rotate-180 pl-1"
                  }
                />
              )}
              <span>
                {dc.getDynamicReplacement(
                  dataJSON.subtext.replace("{{", "").replace("}}", ""),
                )}
              </span>
              {dataJSON.globallySignificant &&
                !dataJSON.subtext.includes("co2_savings_card") && (
                  <Icon
                    name="statisticalSignificance"
                    size="sm"
                    backgroundColor="transparent"
                    padding={false}
                    centered={false}
                    className="pl-4"
                  />
                )}
            </div>
          </div>
        )}
      {equivalence && (
        <div className="flex items-center">
          <ImageWrapper src={equivalence?.iconUrl} size="sm" />
          {equivalence?.equivalenceText}
        </div>
      )}
    </div>
  );
};

const BehaviourCards = (props) => {
  const { dataJSON, layoutData, behaviours, behaviourCardClickThroughHandler } =
    props;
  const isCarousel = layoutData.behaviourCode === "all";
  const handleClick =
    typeof behaviourCardClickThroughHandler === "function"
      ? (behaviourAcronym) => {
          if (behaviourAcronym && behaviourCardClickThroughHandler) {
            behaviourCardClickThroughHandler(behaviourAcronym);
          }
        }
      : undefined;
  if (isCarousel) {
    const sortedBehaviours = [...behaviours].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    const cards = sortedBehaviours
      .filter((bvr) =>
        dataJSON.some((d) => d.behaviour === bvr.behaviourAcronym),
      )
      .map((bvr) => {
        const bvrData = dataJSON.find(
          (d) => d.behaviour === bvr.behaviourAcronym,
        );
        return (
          <div
            className="h-full"
            style={{ width: "255px" }}
            key={bvr.behaviourAcronym}
          >
            <ManagersBehaviourChangeCard
              behaviourName={bvr.behaviourName}
              behaviourAcronym={bvrData.behaviour}
              clickHandler={() => handleClick(bvr.behaviourAcronym)}
              behaviourColour={getBehaviourColourForPosition(bvr.order)?.name}
              attributeText="Average success rate:"
              postSignolValue={bvrData.value * 100}
              postSignolStandardError={bvrData.errorMargin * 100}
              changeVsPreValue={bvrData.delta * 100}
              changeVsPreStandardError={bvrData.deltaErrorMargin * 100}
              significant={bvrData.significant}
            />
          </div>
        );
      });
    return (
      <>
        {dataJSON.length > 4 ? (
          <CarouselSignol infinite={true}>{cards}</CarouselSignol>
        ) : (
          <div className="w-full flex justify-between">{cards}</div>
        )}
      </>
    );
  } else {
    const bvr = behaviours.filter(
      (bvr) => bvr.behaviourAcronym === dataJSON.behaviour,
    )[0];
    return (
      <ManagersBehaviourChangeCard
        behaviourName={bvr.behaviourName}
        behaviourAcronym={dataJSON.behaviour}
        clickHandler={() => handleClick(bvr.behaviourAcronym)}
        behaviourColour={getBehaviourColourForPosition(bvr.order)?.name}
        attributeText="Average success rate:"
        postSignolValue={dataJSON.value * 100}
        postSignolStandardError={dataJSON.errorMargin * 100}
        changeVsPreValue={dataJSON.delta * 100}
        changeVsPreStandardError={dataJSON.deltaErrorMargin * 100}
        significant={dataJSON.significant}
      />
    );
  }
};

const BehiveDataCard = (props) => {
  const {
    layoutData,
    dataJSON,
    industry,
    customerCode,
    behaviours,
    behaviourCardClickThroughHandler,
  } = props;
  const isBehaviourCard =
    layoutData.contentType === "behaviour_data" &&
    (dataJSON.behaviour || dataJSON[0]?.behaviour);

  if (!dataJSON?.equivalency && dataJSON.co2Saved < 1) {
    console.log(
      "Skipping card rendering due to low CO2 savings: ",
      dataJSON.co2Saved,
    );
    return;
  }

  if (dataJSON?.roi_percentage <= 0) {
    console.log(
      "Skipping card rendering due to negative ROI: ",
      dataJSON.roi_percentage,
    );
    return;
  }

  return (
    <div className="w-full h-full">
      {isBehaviourCard ? (
        <BehaviourCards
          layoutData={layoutData}
          dataJSON={dataJSON}
          behaviours={behaviours}
          behaviourCardClickThroughHandler={behaviourCardClickThroughHandler}
        />
      ) : (
        <CardContainer className="w-full" shadow={false}>
          <BasicCardContent
            layoutData={layoutData}
            dataJSON={dataJSON}
            industry={industry}
            customerCode={customerCode}
          />
        </CardContainer>
      )}
    </div>
  );
};
export default BehiveDataCard;
